import "./insHighcharts.scss";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts/highstock";
import { isEqual } from "lodash";
import React, { forwardRef, memo, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useWidgetChildrenId } from "../hooks/use-current-widget";
import { useResizeHighchart } from "./hooks";
import { setCurrentStatusUnselectedLegends } from "store/current-dashboard";
const colorSet = {
    benchmark: "benchmark",
    blueVariation: "blue-variation-color-chart",
    default: "default-color-chart",
    defaultExtended: "default-extended-color-chart",
    dualDefault: "dual-default-color-chart",
    dualGreenRed: "dual-green-red-color-chart",
    esg: "esg-color-chart",
    esgKpiWidget: "esg-color-kpi-widget-chart",
    greenBlueVariation: "green-blue-variation-color-chart",
    orangeVariation: "orange-variation-color-chart",
    purpleVariation: "purple-variation-color-chart"
};
const defaultOptions = {
    chart: {
        // eslint-disable-next-line no-magic-numbers
        spacing: [16, 10, 16, 10]
    },
    legend: {
        useHTML: false
    },
    tooltip: {
        outside: true,
        padding: 0,
        useHTML: true
    }
};
const generateSvgDefs = (colors, colorClass, maximized) => {
    const defs = {};
    for (const color of colors) {
        const id = `highcharts-striped-color-${colorClass}-${color}${maximized ? "-max" : ""}`;
        defs[id] = {
            attributes: {
                class: `highcharts-color-${color}`,
                height: 5,
                id,
                patternUnits: "userSpaceOnUse",
                width: 5
            },
            children: [
                {
                    attributes: { d: "M 0 5 L 5 0 M -0.5 0.5 L 0.5 -0.5 M 4.5 5.5 L 5.5 4.5" },
                    tagName: "path"
                }
            ],
            tagName: "pattern"
        };
    }
    return defs;
};
const InsHighchartsComponent = (props, ref) => {
    const tempChartRef = useRef(null), tempWrapperRef = useRef(null);
    const finalChartRef = ref || tempChartRef, finalWrapperRef = props.wrapperRef || tempWrapperRef;
    const { sectionId, widgetId, uniqueKey, isMaximizedOne } = props, childrenId = useWidgetChildrenId(sectionId, widgetId), currentStatus = useSelector((state) => state.currentDashboard.currentStatus.sections?.[sectionId || ""]?.widgets?.[widgetId || ""], isEqual), dispatch = useDispatch(), maximizedClass = isMaximizedOne ? "highcharts-full-screen" : "", plotSeriesOptions = useMemo(() => (sectionId && widgetId
        ? {
            events: {
                legendItemClick: function legendItemClick(event) {
                    dispatch(setCurrentStatusUnselectedLegends({ childrenId,
                        legendName: event.target.userOptions.id || event.target.userOptions.name || "",
                        sectionId,
                        selected: !this.visible,
                        widgetId }));
                    event.preventDefault();
                }
            }
        }
        : {}), [childrenId, dispatch, sectionId, widgetId]);
    const finalOptions = useMemo(() => {
        const opt = {
            ...defaultOptions,
            ...props.options,
            chart: {
                ...defaultOptions.chart,
                ...props.options?.chart
            },
            defs: {
                ...generateSvgDefs(props.stripedColors || [], props.colorSet ? colorSet[props.colorSet] : "", isMaximizedOne),
                ...props.options?.defs
            },
            legend: {
                ...defaultOptions.legend,
                ...props.options?.legend
            },
            plotOptions: {
                ...props.options?.plotOptions,
                series: { ...plotSeriesOptions, ...props.options?.plotOptions?.series }
            },
            tooltip: {
                ...defaultOptions.tooltip,
                ...props.options?.tooltip
            }
        };
        opt.series?.forEach((serie) => {
            serie.visible = !currentStatus?.unSelectedLegends?.includes(serie.id || serie.name || "");
        });
        return opt;
    }, [currentStatus, plotSeriesOptions, props.options, props.colorSet, props.stripedColors, isMaximizedOne]);
    const containerClass = useMemo(() => ({ className: `${props.containerClassName ?? ""} highchartsContainer ${props.colorSet ? colorSet[props.colorSet] : ""} ${maximizedClass}` }), [props.colorSet, props.containerClassName, maximizedClass]);
    useResizeHighchart(finalChartRef, finalWrapperRef, `${sectionId}.${widgetId}.${uniqueKey}.${isMaximizedOne}`);
    return React.createElement("div", { ref: finalWrapperRef, className: "highchartsWrapper" },
        React.createElement(HighchartsReact, { ref: finalChartRef, ...props, highcharts: Highcharts, options: finalOptions, containerProps: containerClass }));
};
export const InsHighcharts = memo(forwardRef(InsHighchartsComponent));
